import React, { useEffect, useState } from 'react';
import styles from './SalaEspera.module.css';
import HBox from '../../components/layout/HBox';
import CardLigacao from './CardLigacao';
import { Droppable } from 'react-beautiful-dnd';
import { Tooltip } from '../../components/layout/Tooltip';
import Icon from '@mdi/react';
import { mdiInformationSlabBox } from '@mdi/js';
import Descricao from '../../components/tipografia/Descricao';

const getListStyle = (isDraggingOver: boolean, isDropDisabled: boolean, isOffline: boolean, isBusy: boolean) => ({
    background: isDraggingOver
        ? 'var(--tc-color-primary-100)'
        : isOffline ? 'var(--tc-color-danger-50)'
            : isBusy ? 'var(--tc-color-warning-50)'
                : 'var(--tc-color-gray-200)',
    padding: '4px',
    display: 'flex',
    overflow: 'auto',
    minHeight: '80px',
    minWidth: '225px',
    width: '100%',
    borderRadius: '16px',
    gap: '4px',
});

type SalaEsperaProps = {
    nome: string;
    descricao: string;
    ligacoes?: any[];
    dragItem: any;
    droppableId: string;
    online?: boolean;
    requiresGrupo?: boolean;
    hasOnlineAttendants?: boolean;
    chamadaAtiva?: number | null;
    canEnterChamada?: (item: any) => boolean;
    aceitarLigacao?: (chamadaId: number, participantKey: string, livekitToken?: string, livekitUrl?: string) => void;
    encerrarLigacao?: (solucionado?: boolean) => void;
};

const SalaEspera = ({
    nome,
    descricao,
    ligacoes,
    dragItem,
    droppableId,
    online,
    requiresGrupo = false,
    hasOnlineAttendants,
    chamadaAtiva,
    canEnterChamada,
    aceitarLigacao,
    encerrarLigacao,
}: SalaEsperaProps) => {
    // deal with StrictMode...
    const [enabled, setEnabled] = useState(false);

    useEffect(() => {
        const animation = requestAnimationFrame(() => setEnabled(true));

        return () => {
            cancelAnimationFrame(animation);
            setEnabled(false);
        };
    }, []);

    if (!enabled) {
        return null;
    }

    const isWaiting = droppableId === 'aguardando';
    const isGroup = !isWaiting && !online && online === undefined;
    const isUser = online !== undefined;

    const userHasActiveCall = isUser && (ligacoes?.length ?? 0) > 0;

    let tooltipText: string;
    if (isWaiting) {
        tooltipText = 'Sala de espera (ligações aguardando atendimento)';
    } else if (isGroup) {
        tooltipText = hasOnlineAttendants
            ? 'Grupo / Departamento com ramais online'
            : 'Grupo / Departamento sem ramais online';
    } else if (!online) {
        tooltipText = 'Ramal offline';
    } else if (userHasActiveCall) {
        tooltipText = 'Ramal ocupado (ligação em andamento)';
    } else {
        tooltipText = 'Ramal online e disponível';
    }

    const nomeColor = !isUser || online ? undefined : 'var(--tc-color-danger-600)';
    const dropDisabled =
        (requiresGrupo && (!dragItem || dragItem?.grupo_responsavel_id == null)) ||
        (isUser && !online) ||
        (isUser && userHasActiveCall);

    const isOffline = !isWaiting && (
        (isUser && !online) ||
        (isGroup && hasOnlineAttendants === false)
    );
    const isBusy = isUser && !!online && userHasActiveCall;

    const hallClassName = isWaiting
        ? styles.hallEntradaWaiting
        : isGroup
            ? (hasOnlineAttendants === false ? styles.hallEntradaGroupOffline : styles.hallEntradaGroup)
            : online
                ? (userHasActiveCall ? styles.hallEntradaUserBusy : styles.hallEntradaUserOnline)
                : styles.hallEntradaUserOffline;

    return (
        <HBox>
            <div className={hallClassName}>
                <Descricao>
                    <HBox gap="2px">
                        <Tooltip position="right" text={tooltipText}>
                            <Icon
                                path={mdiInformationSlabBox}
                                size="14px"
                                color="var(--tc-color-gray-500)"
                            />
                        </Tooltip>
                        <span
                            style={{
                                color: nomeColor,
                                fontWeight: 600,
                                fontSize: '16px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {nome}
                        </span>
                    </HBox>
                </Descricao>
                <div style={{ textAlign: 'center', fontSize: '12px' }}>{descricao}</div>
            </div>
            <HBox gap="12px" style={{ flex: 1 }}>
                <Droppable
                    droppableId={droppableId}
                    direction="horizontal"
                    isDropDisabled={dropDisabled}
                >
                    {(provided, snapshot) => (
                        <div
                            ref={provided.innerRef}
                            style={{
                                ...getListStyle(
                                    snapshot.isDraggingOver,
                                    dropDisabled,
                                    isOffline,
                                    isBusy,
                                ),
                            }}
                        >
                            {ligacoes?.map((ligacao, idx) => (
                                <CardLigacao
                                    key={`${ligacao.id}-${idx}`}
                                    item={ligacao}
                                    index={idx}
                                    dragItem={dragItem}
                                    isAtiva={chamadaAtiva === ligacao.id}
                                    canEnter={canEnterChamada ? canEnterChamada(ligacao) : false}
                                    aceitarLigacao={aceitarLigacao}
                                    encerrarLigacao={encerrarLigacao}
                                />
                            ))}

                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </HBox>
        </HBox>
    );
};

export default SalaEspera;
